import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Layout } from "./Layout";

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
	children: React.ReactNode;
	requiredRoles?: string[];
}

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication and optional role-based access control.
 * Redirects to /login if user is not authenticated.
 * Redirects to /entries or shows access denied if user lacks required role.
 *
 * @param children - React components to wrap with protection
 * @param requiredRoles - Optional array of role strings that user must have
 *
 * @example
 * ```tsx
 * // Basic authentication check
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // With role requirement
 * <ProtectedRoute requiredRoles={["superadmin", "editor"]}>
 *   <UserManagement />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
	const { user, loading } = useAuth();

	// Show loading state while checking authentication
	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	// Redirect to login if not authenticated
	if (!user) {
		return <Navigate to="/login" replace />;
	}

	// Check role requirements if specified
	if (requiredRoles && requiredRoles.length > 0) {
		const userRole = user.role;

		// Check if user has required role (case-sensitive exact match)
		if (!userRole || !requiredRoles.includes(userRole)) {
			// Redirect to entries page with access denied indication
			return <Navigate to="/entries" replace state={{ accessDenied: true }} />;
		}
	}

	// Render children if authenticated and role requirements met
	return <Layout>{children}</Layout>;
}
